import React, { useState, useMemo } from 'react';
import type { FlattenedSession } from '../../hooks/useAdminData';
import {
  computeEventReport,
  generateMarkdownReport,
  filterSessionsByPeriod,
  type ReportPeriod,
} from '../../utils/reportUtils';
import { formatDuration } from '../../utils/formatTime';
import { getTodayLocalDate, formatDisplayDate } from '../../utils/dateUtils';
import { Button } from '../shared/Button';

interface EventReportViewProps {
  sessions: FlattenedSession[];
}

export function EventReportView({ sessions }: EventReportViewProps) {
  const [period, setPeriod] = useState<ReportPeriod>('all');
  const [selectedDay, setSelectedDay] = useState<string>(() => getTodayLocalDate());
  const [copied, setCopied] = useState(false);

  // Filtered sessions based on period
  const filteredSessions = useMemo(() => {
    return filterSessionsByPeriod(sessions, period, selectedDay);
  }, [sessions, period, selectedDay]);

  // Period label
  const periodLabel = useMemo(() => {
    if (period === 'all') return 'Global (Tout l\'événement)';
    if (period === 'today') return `Aujourd'hui (${formatDisplayDate(getTodayLocalDate())})`;
    return `Journée du ${formatDisplayDate(selectedDay)}`;
  }, [period, selectedDay]);

  // Summary calculation
  const summary = useMemo(() => {
    return computeEventReport(filteredSessions, periodLabel);
  }, [filteredSessions, periodLabel]);

  // Actions
  const handlePrint = () => {
    window.print();
  };

  const handleCopyMarkdown = async () => {
    const md = generateMarkdownReport(summary);
    try {
      await navigator.clipboard.writeText(md);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
    }
  };

  const handleDownloadMarkdown = () => {
    const md = generateMarkdownReport(summary);
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bilan-namm-${selectedDay}.md`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="space-y-8 print:space-y-4 print:text-slate-900 print:bg-white">
      {/* Top Filter & Export Bar (Hidden on print) */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 border border-namm-line/30 bg-namm-panel/70 print:hidden">
        {/* Period Selector */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase text-namm-muted mr-1 font-mono">Période :</span>
          <button
            onClick={() => setPeriod('all')}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors border ${
              period === 'all'
                ? 'border-namm-cyan bg-namm-cyan/20 text-namm-cyan font-semibold'
                : 'border-namm-line/40 text-namm-muted hover:text-namm-text'
            }`}
          >
            Tout l'événement
          </button>
          <button
            onClick={() => setPeriod('today')}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors border ${
              period === 'today'
                ? 'border-namm-cyan bg-namm-cyan/20 text-namm-cyan font-semibold'
                : 'border-namm-line/40 text-namm-muted hover:text-namm-text'
            }`}
          >
            Aujourd'hui
          </button>
          <button
            onClick={() => setPeriod('custom_day')}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors border ${
              period === 'custom_day'
                ? 'border-namm-cyan bg-namm-cyan/20 text-namm-cyan font-semibold'
                : 'border-namm-line/40 text-namm-muted hover:text-namm-text'
            }`}
          >
            Date précise
          </button>

          {period === 'custom_day' && (
            <input
              type="date"
              value={selectedDay}
              onChange={(e) => e.target.value && setSelectedDay(e.target.value)}
              className="bg-namm-panel-soft border border-namm-line/60 px-3 py-1 text-xs text-namm-cyan outline-none focus:border-namm-cyan"
            />
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopyMarkdown}
            title="Copier le résumé pour Discord, Slack ou email"
          >
            {copied ? '✓ Copié !' : '📋 Copier Markdown'}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDownloadMarkdown}
            title="Télécharger le fichier .md"
          >
            📥 Télécharger .md
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handlePrint}
            title="Imprimer ou enregistrer en PDF via le navigateur"
          >
            🖨️ Imprimer / PDF
          </Button>
        </div>
      </div>

      {/* Printable Report Canvas */}
      <div className="space-y-6 print:space-y-4 print:text-slate-900 print:bg-white">
        {/* Report Header */}
        <div className="border-b border-namm-line/30 pb-4 print:border-slate-300">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-namm-cyan font-mono font-bold block print:text-blue-700">
                Frame Perfect — namm Session Timer
              </span>
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-namm-text print:text-slate-900">
                Bilan & Synthèse d'Événement
              </h2>
            </div>
            <div className="text-right">
              <span className="text-xs text-namm-muted block font-mono print:text-slate-600">
                Période : <span className="text-namm-text font-semibold print:text-slate-900">{periodLabel}</span>
              </span>
              <span className="text-[10px] text-namm-muted block font-mono print:text-slate-500">
                {filteredSessions.length} session{filteredSessions.length > 1 ? 's' : ''} analysée{filteredSessions.length > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        {filteredSessions.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-namm-line/30 bg-namm-panel/30 print:border-slate-300 print:bg-slate-50">
            <div className="text-4xl mb-3">📊</div>
            <p className="text-sm font-heading uppercase tracking-wide text-namm-text print:text-slate-800">
              Aucune session enregistrée pour cette période
            </p>
            <p className="text-xs text-namm-muted mt-1 print:text-slate-500">
              Choisis une autre période ou "Tout l'événement" pour visualiser les données.
            </p>
          </div>
        ) : (
          <>
            {/* Key KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:grid-cols-4">
              <div className="p-4 border border-namm-line/30 bg-namm-panel/60 print:border-slate-300 print:bg-slate-50">
                <span className="text-[10px] uppercase tracking-wider text-namm-muted block print:text-slate-600">
                  Temps de jeu total
                </span>
                <span className="text-2xl font-heading font-bold text-green-400 mt-1 block tabular-nums print:text-emerald-800">
                  {formatDuration(summary.totalDurationMs)}
                </span>
                <span className="text-[10px] text-namm-muted mt-1 block print:text-slate-500">
                  Cumulé sur l'ensemble des postes
                </span>
              </div>

              <div className="p-4 border border-namm-line/30 bg-namm-panel/60 print:border-slate-300 print:bg-slate-50">
                <span className="text-[10px] uppercase tracking-wider text-namm-muted block print:text-slate-600">
                  Sessions jouées
                </span>
                <span className="text-2xl font-heading font-bold text-namm-cyan mt-1 block print:text-blue-800">
                  {summary.totalSessions}
                </span>
                <span className="text-[10px] text-namm-muted mt-1 block print:text-slate-500">
                  {summary.countdownCount} chronométrées · {summary.stopwatchCount} libres
                </span>
              </div>

              <div className="p-4 border border-namm-line/30 bg-namm-panel/60 print:border-slate-300 print:bg-slate-50">
                <span className="text-[10px] uppercase tracking-wider text-namm-muted block print:text-slate-600">
                  Durée moyenne / session
                </span>
                <span className="text-2xl font-heading font-bold text-namm-text mt-1 block tabular-nums print:text-slate-900">
                  {formatDuration(summary.averageSessionDurationMs)}
                </span>
                <span className="text-[10px] text-namm-muted mt-1 block print:text-slate-500">
                  Temps effectif par joueur
                </span>
              </div>

              <div className="p-4 border border-namm-line/30 bg-namm-panel/60 print:border-slate-300 print:bg-slate-50">
                <span className="text-[10px] uppercase tracking-wider text-namm-muted block print:text-slate-600">
                  Postes & Staff
                </span>
                <span className="text-2xl font-heading font-bold text-violet-400 mt-1 block print:text-purple-800">
                  {summary.uniqueRoomsCount} <span className="text-xs text-namm-muted print:text-slate-500">postes</span> · {summary.uniqueStaffCount} <span className="text-xs text-namm-muted print:text-slate-500">staff</span>
                </span>
                <span className="text-[10px] text-namm-muted mt-1 block print:text-slate-500">
                  Activité multi-salles
                </span>
              </div>
            </div>

            {/* Top Games Section */}
            <div className="border border-namm-line/30 bg-namm-panel/50 p-5 print:border-slate-300 print:bg-white">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-namm-line/20 print:border-slate-200">
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-namm-cyan print:text-blue-800">
                  🏆 Palmarès des Jeux les plus joués
                </h3>
                <span className="text-xs text-namm-muted font-mono print:text-slate-500">
                  {summary.topGames.length} jeu{summary.topGames.length > 1 ? 'x' : ''} recensé{summary.topGames.length > 1 ? 's' : ''}
                </span>
              </div>

              <div className="space-y-3">
                {summary.topGames.map((g, idx) => (
                  <div key={g.game} className="space-y-1">
                    <div className="flex items-center justify-between text-xs font-mono">
                      <div className="flex items-center gap-2">
                        <span className="w-5 text-namm-muted font-bold print:text-slate-600">#{idx + 1}</span>
                        <span className="text-namm-text font-bold uppercase print:text-slate-900">
                          {g.game}
                        </span>
                        <span className="text-[10px] text-namm-muted print:text-slate-500">
                          ({g.sessionCount} session{g.sessionCount > 1 ? 's' : ''})
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-green-400 font-semibold print:text-emerald-800">
                          {formatDuration(g.totalDurationMs)}
                        </span>
                        <span className="text-namm-muted text-[10px] w-10 text-right print:text-slate-600 font-bold">
                          {g.percentage}%
                        </span>
                      </div>
                    </div>
                    {/* Visual Progress Bar */}
                    <div className="w-full bg-namm-panel-soft h-2.5 rounded overflow-hidden border border-namm-line/20 print:bg-slate-200 print:border-slate-300">
                      <div
                        className="bg-gradient-to-r from-namm-cyan to-namm-violet h-full rounded transition-all print:bg-blue-600"
                        style={{ width: `${Math.max(g.percentage, 2)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Room / Poste Activity Breakdown */}
            <div className="border border-namm-line/30 bg-namm-panel/50 p-5 print:border-slate-300 print:bg-white">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-namm-line/20 print:border-slate-200">
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-namm-cyan print:text-blue-800">
                  🖥️ Activité par Poste / Salle
                </h3>
                <span className="text-xs text-namm-muted font-mono print:text-slate-500">
                  {summary.roomStats.length} salle{summary.roomStats.length > 1 ? 's' : ''}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono text-left print:border-collapse">
                  <thead>
                    <tr className="border-b border-namm-line/30 text-namm-muted uppercase text-[10px] print:border-slate-300 print:text-slate-700 print:bg-slate-100">
                      <th className="py-2.5 px-3">Poste / Salle</th>
                      <th className="py-2.5 px-3">Sessions</th>
                      <th className="py-2.5 px-3">Temps Cumulé</th>
                      <th className="py-2.5 px-3">% Global</th>
                      <th className="py-2.5 px-3">Jeu favori</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-namm-line/15 print:divide-slate-200">
                    {summary.roomStats.map((r) => (
                      <tr key={r.roomId} className="hover:bg-namm-panel-soft/30 transition-colors print:bg-white">
                        <td className="py-2.5 px-3 font-heading font-bold uppercase text-namm-text print:text-slate-900">
                          {r.roomName}
                        </td>
                        <td className="py-2.5 px-3 text-namm-muted print:text-slate-700 font-semibold">
                          {r.sessionCount}
                        </td>
                        <td className="py-2.5 px-3 text-green-400 font-semibold print:text-emerald-800">
                          {formatDuration(r.totalDurationMs)}
                        </td>
                        <td className="py-2.5 px-3 text-namm-muted print:text-slate-700 font-semibold">
                          {r.percentage}%
                        </td>
                        <td className="py-2.5 px-3 text-namm-cyan print:text-blue-800 font-semibold">
                          {r.topGame || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Print Footer */}
            <div className="hidden print:block text-center pt-4 border-t border-slate-300 text-[10px] text-slate-500 font-mono">
              Rapport généré automatiquement par namm Session Timer (Frame Perfect) — Document confidentiel / Association Frame Perfect
            </div>
          </>
        )}
      </div>
    </div>
  );
}
